import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddresses } from '../context/AddressContext';
import api from '../api';
import '../pages/ClosingPacks.css';

// Shared rendering for the Closing Packs and Settlement Statements tabs.
// Reads locally-stored copies (synced from Google Drive when addresses are
// saved) instead of re-querying Drive every visit. A per-address Refresh
// button kicks off a manual sync if the user wants the latest version.
export default function AddressFilesView({ kind, title, emptyKindLabel }) {
  const { id: packageId } = useParams();
  const { addresses } = useAddresses();
  const [filesByAddress, setFilesByAddress] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});

  const validAddresses = addresses.filter(a => a.trim());

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/address-files', { params: { packageId, kind } });
      const grouped = {};
      for (const f of res.data.files || []) {
        if (!grouped[f.address]) grouped[f.address] = [];
        grouped[f.address].push(f);
      }
      setFilesByAddress(grouped);
    } catch (err) {
      console.error('Failed to load address files:', err);
    } finally {
      setLoading(false);
    }
  }, [packageId, kind]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleRefresh = async (address) => {
    setRefreshing(r => ({ ...r, [address]: true }));
    try {
      await api.post('/address-files/sync', { packageId, address });
      await loadFiles();
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(r => ({ ...r, [address]: false }));
    }
  };

  const getStreetAddress = (address) => address.split(',')[0].trim();

  const openFile = async (file, e) => {
    e.preventDefault();
    try {
      const res = await api.get(`/address-files/${file.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to open file:', err);
    }
  };

  return (
    <div className="closing-packs-page">
      <h1 className="page-title">{title}</h1>

      {validAddresses.length === 0 ? (
        <div className="warning-box">No addresses saved. Go to the Addresses tab to add addresses first.</div>
      ) : (
        <div className="closing-packs-list">
          {validAddresses.map((addr, i) => {
            const street = getStreetAddress(addr);
            const files = filesByAddress[addr] || [];
            const isRefreshing = !!refreshing[addr];

            return (
              <div key={i} className="closing-pack-card">
                <div className="card-header">
                  <h3 className="card-address">{street}</h3>
                  <button
                    className="btn-refresh"
                    onClick={() => handleRefresh(addr)}
                    disabled={isRefreshing}
                    title="Re-sync from Google Drive"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                      <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                    </svg>
                  </button>
                </div>

                <div className="card-body">
                  {isRefreshing && (
                    <div className="card-status searching">Re-syncing from Google Drive...</div>
                  )}
                  {!isRefreshing && loading && files.length === 0 && (
                    <div className="card-status searching">Loading...</div>
                  )}
                  {!isRefreshing && !loading && files.length === 0 && (
                    <div className="card-status not-found">
                      No {emptyKindLabel} stored for "{street}". Click refresh to pull from Drive.
                    </div>
                  )}
                  {files.length > 0 && (
                    <div className="card-files">
                      {files.map(file => (
                        <a
                          key={file.id}
                          href="#open"
                          onClick={(e) => openFile(file, e)}
                          className="file-link"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="file-icon">
                            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zM9.5 3A1.5 1.5 0 0 0 8 1.5V3h1.5z"/>
                          </svg>
                          <div className="file-info">
                            <span className="file-name">{file.file_name}</span>
                            <span className="file-date">
                              Synced {new Date(file.fetched_at).toLocaleDateString()}
                            </span>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="link-icon">
                            <path fillRule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
                            <path fillRule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
                          </svg>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
